<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container fluid>
        <!-- Desktop toolbar (hidden on mobile) -->
        <b-row align-h="between" align-v="center" class="d-none d-md-flex">
          <b-col md="5" lg="4" class="p-3 mb-0">
            <b-form-input
              v-model="search"
              placeholder="Search trade name..."
              debounce="500"
            />
          </b-col>
          <b-col cols="auto" class="pr-3">
            <b-button
              v-if="canCreateTrade"
              variant="primary"
              class="pl-2 pr-3"
              @click="addingTrade(true)"
            >
              <b-icon-plus font-scale="1.5" class="align-middle"></b-icon-plus>
              <span class="align-middle">Add New Trade</span>
            </b-button>
          </b-col>
        </b-row>

        <!-- Mobile toolbar (hidden on desktop) -->
        <div
          class="d-md-none d-flex mt-3 px-0"
          style="gap: 0.4rem; align-items: stretch"
        >
          <b-form-input
            v-model="search"
            placeholder="Search trade name..."
            debounce="500"
            class="flex-grow-1"
          />
          <b-button
            v-if="canCreateTrade"
            variant="primary"
            class="trade-mobile-btn"
            @click="addingTrade(true)"
          >
            <b-icon-plus class="align-middle" />
          </b-button>
        </div>

        <!-- Mobile: compact trade rows (hidden on md+) -->
        <div class="d-md-none pb-2 mt-2">
          <div
            v-for="item in trades"
            :key="item.id"
            class="mobile-trade-item"
            :class="canCreateTrade ? 'c-pointer' : ''"
            @click="onTradeRowClicked(item, 0)"
          >
            <span class="mobile-trade__name">{{ item.name || '-' }}</span>
            <div
              v-if="item.templates && item.templates.length"
              class="mobile-trade__meta"
            >
              {{
                item.templates
                  .slice(0, 3)
                  .map((t) => t.label)
                  .join(', ')
              }}{{ item.templates.length > 3 ? '...' : '' }}
            </div>
            <div v-else class="mobile-trade__meta">No required files</div>
          </div>
          <div
            v-if="!trades || !trades.length"
            class="text-center py-4 text-muted"
          >
            No trades found.
          </div>
        </div>

        <!-- Desktop: full table (hidden below md) -->
        <div class="d-none d-md-block">
          <b-table
            id="my-table"
            class="mt-4"
            responsive
            hover
            show-empty
            ref="tradesTable"
            :fields="fields"
            :items="trades"
            :tbody-tr-class="canCreateTrade ? 'trade-row-editable' : ''"
            @row-clicked="onTradeRowClicked"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <digger-loader />
              </div>
            </template>

            <template #cell(requiredFiles)="data">
              <span :id="`tooltip-target-${data.index}`">
                <span
                  v-for="(template, index) in data.item.templates.slice(0, 4)"
                  :key="template.id"
                >
                  {{ index != 3 ? template.label : '...'
                  }}{{
                    index != data.item.templates.slice(0, 4).length - 1
                      ? ', '
                      : ''
                  }}
                </span>
              </span>
              <b-tooltip
                :target="`tooltip-target-${data.index}`"
                triggers="hover"
                class="w-max"
              >
                <p
                  class="px-1 pb-1 m-0 text-left font-weight-bold text-nowrap"
                  style="text-decoration: underline"
                >
                  {{ data.item.name }}:
                </p>
                <p
                  v-for="template in data.item.templates"
                  :key="template.id"
                  class="px-2 m-0 text-left"
                >
                  *. {{ template.label }}
                </p>
                <div class="pb-1"></div>
              </b-tooltip>
            </template>
          </b-table>
        </div>

        <b-pagination
          v-if="rows > perPage"
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          aria-controls="my-table"
          align="center"
        />

        <p v-if="tradesMeta" align="center">
          Showing {{ trades.length }} out of total of
          {{ tradesMeta.total }} trades
        </p>

        <!-- Dialog - Adding / Updating Trade -->
        <b-modal
          v-model="isAddingOrUpdatingTrade"
          :title="`${isAddingTrade ? 'Add New' : 'Edit'} Trade`"
          centered
          :hide-footer="true"
        >
          <b-form @submit.prevent="onAddOrUpdateTrade">
            <b-form-group label="Trade Name">
              <b-form-input
                v-model="trade.name"
                placeholder="Enter Trade Name"
                :readonly="!canCreateTrade"
                required
              ></b-form-input>
            </b-form-group>

            <div class="pb-2 pb-md-4"></div>

            <b-row class="px-3 pb-2" align-v="center">
              <b-col cols="6" class="m-0">
                <h4 class="m-0 font-weight-bold required-files-title">
                  Required Files
                </h4>
              </b-col>
              <b-col cols="6" class="m-0">
                <b-form-input
                  v-model="searchRequiredFiles"
                  placeholder="Search..."
                  debounce="500"
                />
              </b-col>
            </b-row>

            <hr class="m-0" />

            <required-files-list
              class="mb-4"
              :searchRequiredFiles="searchRequiredFiles"
              :templateIds="selectedTemplateIds"
              :disabled="!canCreateTrade"
              @templateSelected="onTemplateSelected"
            />

            <div
              v-if="canCreateTrade"
              class="d-flex flex-wrap justify-content-end p-2 trade-modal-footer"
            >
              <!-- Reset -->
              <b-button
                v-if="trade.trade_id && trade.company_id"
                variant="danger"
                class="bg-error"
                @click="isResettingTrade = true"
              >
                RESET
              </b-button>

              <!-- Delete -->
              <b-button
                v-if="!trade.trade_id && trade.company_id"
                variant="danger"
                @click="isDeletingTrade = true"
              >
                DELETE
              </b-button>

              <!-- Cancel -->
              <b-button
                variant="outline-secondary"
                @click="isAddingOrUpdatingTrade = false"
              >
                CANCEL
              </b-button>

              <!-- Submit -->
              <b-button type="submit" variant="primary"> SUBMIT </b-button>
            </div>
          </b-form>
        </b-modal>

        <!-- Dialog - Reset / Delete Trade -->
        <b-modal
          v-model="isResettingOrDeletingTrade"
          :title="`${isResettingTrade ? 'Reset' : 'Delete'} Trade`"
          centered
          :hide-footer="true"
        >
          <b-row>
            <b-col>
              <p>
                Are you sure to {{ isResettingTrade ? 'reset' : 'delete' }}
                <b>{{ trade.name }}</b> trade ?
              </p>
              <p>This action cannot be undone!</p>
            </b-col>
          </b-row>

          <b-row align-h="end" class="p-2">
            <!-- Cancel -->
            <b-button
              variant="outline-secondary"
              @click="isResettingOrDeletingTrade = false"
              class="mx-2"
            >
              CANCEL
            </b-button>

            <!-- Confirm -->
            <b-button variant="danger" class="mx-2" @click="onDeleteTrade">
              YES
            </b-button>
          </b-row>
        </b-modal>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import RequiredFilesList from '@/components/lists/RequiredFilesList'

export default {
  components: {
    RequiredFilesList,
  },

  data() {
    return {
      perPage: 10,
      currentPage: 1,
      search: '',

      // Trade
      // trade: null,
      searchRequiredFiles: null,

      // Template
      selectedTemplateIds: [],

      // Status Booleans
      isLoading: false,
      isAddingTrade: false,
      isUpdatingTrade: false,
      isResettingTrade: false,
      isDeletingTrade: false,
    }
  },

  computed: {
    ...mapGetters('trade', ['trades', 'tradesMeta', 'trade']),
    ...mapGetters('auth', ['user', 'hasPermission']),

    canCreateTrade() {
      return this.hasPermission('trades.create')
    },

    fields() {
      return [
        {
          key: 'name',
          label: 'Trade Name',
          class: 'w-25',
          sortable: true,
        },
        {
          key: 'requiredFiles',
          label: 'Required Files',
        },
      ]
    },

    rows() {
      return this.tradesMeta ? this.tradesMeta.total : 0
    },

    isAddingOrUpdatingTrade: {
      get: function () {
        return this.isAddingTrade || this.isUpdatingTrade
      },

      set: function (value) {
        this.resetForm()
        this.isAddingTrade = value
        this.isUpdatingTrade = value
      },
    },

    isResettingOrDeletingTrade: {
      get: function () {
        return this.isResettingTrade || this.isDeletingTrade
      },

      set: function (value) {
        this.isResettingTrade = value
        this.isDeletingTrade = value
      },
    },
  },

  watch: {
    currentPage: async function (newCurrentPage) {
      await this.fetchTrades(newCurrentPage, this.perPage, this.search)
    },

    search: async function (newSearch) {
      const page = 1
      if (this.currentPage !== page) {
        this.currentPage = page
      } else {
        await this.fetchTrades(page, this.perPage, newSearch)
      }
    },
  },

  methods: {
    ...mapActions('trade', {
      getTrades: 'getTrades',
      getTrade: 'getTrade',
      createTrade: 'createTrade',
      updateTrade: 'updateTrade',
      deleteTrade: 'deleteTrade',
    }),

    ...mapActions('auth', {
      getAuthenticatedUser: 'getAuthenticatedUser',
    }),

    ...mapMutations('trade', {
      setTrade: 'setTrade',
    }),

    async fetchTrades(page, limit, search) {
      this.isLoading = true

      await this.getTrades({ page, limit, search }).finally(() => {
        this.isLoading = false
      })
    },

    onTradeRowClicked(item, i) {
      if (!this.canCreateTrade) return
      this.viewTrade(item, i)
    },

    async viewTrade(item, i) {
      // console.log(
      //   item.id,
      //   item.trade_id,
      //   item.company_id,
      //   Object.values(item.templates).map((x) => x.id),
      // )

      await this.getTrade({
        id: item.id,
        trade_id: item.trade_id,
        company_id: item.company_id,
      })
        .catch((err) => this.handleError(err))
        .finally(() => {
          this.selectedTemplateIds = this.trade.templates.map((x) => x.id)
          this.updatingTrade(true)
        })
    },

    addingTrade(status) {
      this.resetForm()
      this.isAddingTrade = status
    },

    async onAddOrUpdateTrade() {
      if (this.isAddingTrade) {
        await this.createTrade({
          trade_id: null,
          company_id: this.user.company_id,
          name: this.trade.name,
          template_ids: this.selectedTemplateIds,
        })
          .then((res) => {
            this.$bvToast.toast('Trade created successfully!', {
              title: 'Success',
              autoHideDelay: 3000,
              variant: 'success',
            })
          })
          .catch((err) => this.handleError(err))
          .finally(() => {
            this.addingTrade(false)
            this.fetchTrades(this.currentPage, this.perPage, this.search)
          })
      }

      if (this.isUpdatingTrade) {
        var updateExisting = !this.trade.company_id

        // console.log(JSON.stringify(this.trade))
        // console.log('id: ', updateExisting ? null : this.trade.id)
        // console.log(
        //   'trade_id: ',
        //   updateExisting ? this.trade.id : this.trade.trade_id
        // )
        // console.log('company_id: ', this.user.company_id)
        // console.log('name: ', this.trade.name)
        // console.log('template_ids: ', JSON.stringify(this.selectedTemplateIds))
        // console.log('')

        await this.updateTrade({
          id: updateExisting ? null : this.trade.id,
          trade_id: updateExisting ? this.trade.id : this.trade.trade_id,
          company_id: this.user.company_id,
          name: this.trade.name,
          template_ids: this.selectedTemplateIds,
        })
          .then((res) => {
            this.$bvToast.toast('Trade updated successfully!', {
              title: 'Success',
              autoHideDelay: 3000,
              variant: 'success',
            })
          })
          .catch((err) => this.handleError(err))
          .finally(() => {
            this.updatingTrade(false)
            this.fetchTrades(this.currentPage, this.perPage, this.search)
          })
      }
    },

    updatingTrade(status) {
      this.isUpdatingTrade = status
    },

    onTemplateSelected(templateIds) {
      this.selectedTemplateIds = templateIds
    },

    async onDeleteTrade() {
      await this.deleteTrade(this.trade.id)
        .then((res) => {
          this.$bvToast.toast('Trade DELETED successfully!', {
            title: 'Success',
            autoHideDelay: 3000,
            variant: 'success',
          })
        })
        .catch((err) => this.handleError(err))
        .finally(() => {
          this.isResettingOrDeletingTrade = false
          this.updatingTrade(false)
          this.fetchTrades(this.currentPage, this.perPage, this.search)
        })
    },

    resetForm() {
      this.setTrade({ name: '' })
      this.searchRequiredFiles = ''
      this.selectedTemplateIds = []
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    handleError(err) {
      this.showToast('Oops.. Something went wrong!', err.data.message, 'danger')
    },
  },

  async beforeMount() {
    this.resetForm()
  },

  async mounted() {
    await this.getAuthenticatedUser()
    await this.fetchTrades(this.currentPage, this.perPage, this.search)
  },
}
</script>

<style lang="scss" scoped>
/deep/ table.b-table tr.trade-row-editable:hover {
  cursor: pointer;
}

/* Add/Edit Trade modal: tighten up on small screens. */
.trade-modal-footer {
  gap: 0.5rem;
}

@media (max-width: 575.98px) {
  /* Keep "Required Files" on a single line beside the search box. */
  .required-files-title {
    font-size: 1.1rem;
  }

  /* Footer buttons share the row and shed the wide default padding so
   * Cancel / Submit sit side-by-side instead of stacking. */
  .trade-modal-footer .btn {
    flex: 1 1 auto;
    min-width: 0;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
}

.trade-mobile-btn {
  align-self: stretch;
  height: auto;
  width: 42px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}

.mobile-trade-item {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
}

.mobile-trade__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-trade__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  line-height: 1.4;
}
</style>

<style>
.tooltip-inner {
  max-width: 500px !important;
}
</style>
